"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { HiStar } from "@/components/icons";
import InstallButton, { type Product } from "@/components/InstallButton";

interface HomepageProductCardProps {
  product: {
    id: string;
    slug: string;
    name: string;
    description: string;
    rating: string;
    icon: React.ReactNode;
    iconBg: string;
    backgroundIcon: React.ReactNode;
    backgroundIconOpacity?: string;
    features: Array<{ label: string; value: string }>;
    /** True when the product has paid tiers (offers). Drives the "Get Pro" CTA. */
    hasPaidTier?: boolean;
    /** True when the product is awaiting Chrome Web Store approval. Drives the
     * "Coming Soon" badge, hides the (placeholder) rating, and collapses the
     * dual Get Pro / Details CTAs into a single Learn More link. The Install
     * button itself auto-handles coming-soon via InstallButton's internal swap. */
    comingSoon?: boolean;
  };
}

export default function HomepageProductCard({ product }: HomepageProductCardProps) {
  const router = useRouter();

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only navigate if clicking on the card itself, not on buttons/links
    if ((e.target as HTMLElement).closest("a, button")) {
      return;
    }
    router.push(`/product/${product.slug}`);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      if ((e.target as HTMLElement).closest("a, button")) {
        return;
      }
      e.preventDefault();
      router.push(`/product/${product.slug}`);
    }
  };

  return (
    <div
      className={`group relative overflow-hidden rounded-3xl border bg-card p-8 transition-all flex flex-col cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
        product.comingSoon
          ? "border-dashed border-border/60 hover:border-primary/40"
          : "border-border hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/5"
      }`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="group"
      tabIndex={0}
      aria-label={
        product.comingSoon
          ? `${product.name} — Coming soon, ${product.description}`
          : `${product.name} — ${product.description}`
      }
    >
      <div
        className={`absolute top-0 right-0 p-4 ${product.backgroundIconOpacity || "opacity-5"} group-hover:opacity-10 transition-opacity`}
      >
        {product.backgroundIcon}
      </div>

      {product.comingSoon && (
        <div className="absolute top-4 left-4 z-30 pointer-events-none">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-mono font-bold tracking-widest uppercase border border-primary/20 backdrop-blur-sm">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary"></span>
            </span>
            Coming Soon
          </span>
        </div>
      )}

      <div className="relative z-10 flex-1 flex flex-col pointer-events-none">
        <div className="flex justify-between items-start mb-6">
          <div
            className={`w-12 h-12 ${product.iconBg} rounded-xl flex items-center justify-center text-2xl shadow-lg ${product.comingSoon ? "mt-8" : ""}`}
          >
            {product.icon}
          </div>
          {!product.comingSoon && (
            <div className="flex items-center gap-2 text-lg font-bold text-foreground">
              <HiStar className="text-yellow-500 fill-yellow-500 w-7 h-7" aria-hidden="true" />{" "}
              {product.rating}
            </div>
          )}
        </div>

        <h3 className="text-2xl font-bold mb-2">{product.name}</h3>
        <p className="text-muted-foreground mb-6 flex-grow">{product.description}</p>

        <div className="space-y-3 mb-8 font-mono text-sm text-muted-foreground">
          {product.features.map((feature, idx) => (
            <div key={idx} className="flex justify-between border-b border-border/50 pb-1">
              <span>{feature.label}</span>
              <span className="text-foreground">{feature.value}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3 mt-auto relative z-20 pointer-events-auto">
          {product.comingSoon ? (
            // Coming-soon: single Learn More CTA. The Install button is hidden
            // entirely (its disabled-CTA variant would link to the same place,
            // so showing both would be redundant).
            <Link
              href={`/product/${product.slug}/`}
              className="w-full inline-flex items-center justify-center py-3 rounded-3xl bg-primary text-primary-foreground hover:bg-primary/90 font-bold transition-colors shadow-lg shadow-primary/20"
            >
              Learn More →
            </Link>
          ) : (
            <>
              <InstallButton
                product={product.id as Product}
                className="w-full py-3 rounded-3xl bg-muted text-foreground hover:bg-foreground hover:text-background font-bold transition-colors shadow-sm relative z-20"
              >
                Install Free
              </InstallButton>

              {product.hasPaidTier ? (
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    href={`/pricing/${product.slug}/#pricing-cards`}
                    className="w-full inline-flex items-center justify-center py-3 rounded-3xl bg-primary text-primary-foreground hover:bg-primary/90 font-bold transition-colors shadow-lg shadow-primary/20 text-sm relative z-20"
                  >
                    Get Pro
                  </Link>
                  <Link
                    href={`/product/${product.slug}`}
                    className="w-full inline-flex items-center justify-center py-3 rounded-3xl border border-border hover:bg-secondary transition-colors text-sm font-medium relative z-20"
                  >
                    Details
                  </Link>
                </div>
              ) : (
                // Free-only product: no "Get Pro" CTA (no paid tier exists). Details
                // link spans full width and keeps the card visually balanced.
                <Link
                  href={`/product/${product.slug}`}
                  className="w-full inline-flex items-center justify-center py-3 rounded-3xl border border-border hover:bg-secondary transition-colors text-sm font-medium relative z-20"
                >
                  Details
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
